import { readCopyFlag } from '@/lib/copy-search';

/**
 * Ce que l'URL de l'éditeur d'expérience porte (refonte modulaire §4, §9.1).
 *
 * Three parameters, read by the two routes that mount the editor - the empty
 * one and the one of an existing experience - so the reading is written once,
 * pure, and testable: `vitest.config.ts` excludes `src/routes/**` from the
 * coverage report, and a `validateSearch` written inline in two files would be
 * two spellings of one rule.
 *
 *   - **`?version=`** - « Voir », the read-only opening of §4, the same régime
 *     as on the three editors before it;
 *   - **`?tab=`** - the tab somebody is reading (§9.1). The layer RESOLVES what
 *     it is given: a value naming no tab falls back to the first, so this reader
 *     does not have to know the tab list - and must not, because « Historique »
 *     is the layer's own and not the object's;
 *   - **`?page=`** - the page the Timeline is on (§9.1, « la page sélectionnée
 *     est portée par l'URL »). Carried now, used by the scene of #57 and by the
 *     page editor of #58; this lot marks the named row and lets « Ouvrir » on a
 *     refused line of the save box land on it.
 *
 * ## It NEVER throws
 *
 * `validateSearch` runs before anything renders, so a typo in a pasted link has
 * to degrade to « no parameter » rather than to a blank screen - the rule every
 * filtered screen of this repo already holds. An unknown `?tab=` is left to the
 * layer, which resolves it onto the first tab; an empty `?page=` is dropped.
 *
 * ## The keys are OMITTED, never `undefined`
 *
 * `exactOptionalPropertyTypes` makes `{ tab: undefined }` and `{}` two
 * different types, and TanStack writes every key it is handed into the address
 * bar - so a present key holding `undefined` would put `?tab=` on the URL of
 * every editor opened without one.
 */
export type ExperienceEditorSearch = {
  version?: string;
  tab?: string;
  page?: string;
  /**
   * The `?copie` of §4.2: the editor this parameter opens SELECTS its name
   * field, because a copy is named « X (copie) » and that name is made to be
   * typed over.
   *
   * A flag rather than a string, and read as one: present and true, or absent.
   */
  copie?: true;
};

/** A non-empty string, or undefined - the only shape any of the three take. */
function readParam(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw !== '' ? raw : undefined;
}

export function readExperienceEditorSearch(
  search: Record<string, unknown>,
): ExperienceEditorSearch {
  const parsed: ExperienceEditorSearch = {};
  const version = readParam(search.version);
  if (version !== undefined) parsed.version = version;
  const tab = readParam(search.tab);
  if (tab !== undefined) parsed.tab = tab;
  const page = readParam(search.page);
  if (page !== undefined) parsed.page = page;
  const copie = readCopyFlag(search.copie);
  if (copie !== undefined) parsed.copie = copie;
  return parsed;
}
